import { createApi } from '@reduxjs/toolkit/query/react'
import { createBaseQueryWithReauth } from 'store/auth/base-query-with-reauth'
import {
  CreateExpensePayload,
  Expense,
  ExpenseReferenceOptions,
  ExpenseSummary,
  GetExpenseSummariesParams,
  UpdateExpenseStatusPayload,
} from './expenses.types'

const baseUrl = `${process.env.REACT_APP_ONBOARDING_SERVICE_API}`

export const expensesApi = createApi({
  reducerPath: 'expensesApi',
  baseQuery: createBaseQueryWithReauth(baseUrl!),
  tagTypes: ['Expenses', 'Expense'],
  endpoints: (builder) => ({
    getExpenseSummaries: builder.query<ExpenseSummary[], GetExpenseSummariesParams>({
      query: ({ userId, status }) => {
        const params = new URLSearchParams({ userId })
        if (status) {
          params.append('status', status)
        }
        return { url: `oapi/v1/expenses/summary?${params.toString()}` }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Expenses' as const, id })),
              { type: 'Expenses', id: 'LIST' },
            ]
          : [{ type: 'Expenses', id: 'LIST' }],
    }),

    getExpenseReferenceOptions: builder.query<
      ExpenseReferenceOptions,
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `oapi/v1/expenses/reference-options?userId=${userId}`,
      }),
    }),

    getExpenseById: builder.query<Expense, { expenseId: string }>({
      query: ({ expenseId }) => ({
        url: `oapi/v1/expenses/${expenseId}`,
      }),
      providesTags: (_result, _error, { expenseId }) => [
        { type: 'Expense', id: expenseId },
      ],
    }),

    createExpense: builder.mutation<Expense, CreateExpensePayload>({
      query: ({ userId, payableType, file }) => {
        const formData = new FormData()
        formData.append('userId', userId)
        formData.append('payableType', payableType)
        formData.append('file', file)
        return {
          url: 'oapi/v1/expenses',
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: [{ type: 'Expenses', id: 'LIST' }],
    }),

    updateExpense: builder.mutation<Expense, { expenseId: string; body: Partial<Expense> }>({
      query: ({ expenseId, body }) => ({
        url: `oapi/v1/expenses/${expenseId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { expenseId }) => [
        { type: 'Expense', id: expenseId },
        { type: 'Expenses', id: 'LIST' },
      ],
    }),

    updateExpenseStatus: builder.mutation<Expense, UpdateExpenseStatusPayload>({
      query: ({ id, status }) => ({
        url: `oapi/v1/expenses/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Expense', id },
        { type: 'Expenses', id: 'LIST' },
      ],
    }),

    deleteExpense: builder.mutation<void, { expenseId: string }>({
      query: ({ expenseId }) => ({
        url: `oapi/v1/expenses/${expenseId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Expenses', id: 'LIST' }],
    }),
  }),
})

export const {
  useGetExpenseSummariesQuery,
  useGetExpenseReferenceOptionsQuery,
  useGetExpenseByIdQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useUpdateExpenseStatusMutation,
  useDeleteExpenseMutation,
} = expensesApi
